/**
 * Product Display Component
 * 
 * Displays the subscription product card with checkout button.
 * Handles the checkout session creation and redirects to Stripe.
 */

import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from '../../i18n/hooks';
import { useAuth } from '../../auth/hooks';
import { getEnvironmentConfig } from '../../../config/environment';
import Button from '../../ui/components/Button';
import { globalNotifications } from '../../ui';
import StripeService from '../services/StripeService';
import './ProductDisplay.css';

/**
 * Stripe Price Lookup Key
 */
const STRIPE_LOOKUP_KEY = 'ArbiWin_Premium-05b10d7';

/**
 * Get checkout API URL from environment
 */
const getCheckoutApiUrl = () => {
  const config = getEnvironmentConfig();
  const baseUrl = config.api.baseURL || process.env.REACT_APP_API_BASE_URL || 'http://localhost:80';
  return `${baseUrl}/api/payment/create-checkout-session`;
};

/**
 * ProductDisplay - Subscription product card component
 */
const ProductDisplay = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Handle checkout - creates Stripe checkout session and redirects
   */
  const handleCheckout = useCallback(async () => {
    console.log('🔍 handleCheckout: Starting...');
    
    if (!isAuthenticated) {
      setError(t('stripe.errors.notAuthenticated') || 'Please log in to continue');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get the auth token
      const token = localStorage.getItem('arbiwin_auth_token');
      
      if (!token) {
        setError(t('stripe.errors.notAuthenticated') || 'Please log in to continue');
        setLoading(false);
        return;
      }

      const apiUrl = getCheckoutApiUrl();
      console.log('🔍 handleCheckout: Calling Stripe checkout API at', apiUrl);
      
      // Direct axios call to backend
      const response = await axios.post(
        apiUrl,
        { lookup_key: STRIPE_LOOKUP_KEY },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      console.log('🔍 handleCheckout: Response received:', response.status);

      const checkoutUrl = response?.data?.url;

      if (checkoutUrl && checkoutUrl.includes('stripe.com')) {
        console.log('🔍 handleCheckout: Redirecting to Stripe checkout...');
        window.location.href = checkoutUrl;
      } else {
        console.error('🔍 handleCheckout: Invalid URL in response:', response?.data);
        setError(t('stripe.errors.checkoutFailed') || 'Failed to create checkout session');
        setLoading(false);
      }
    } catch (err) {
      console.error('🔍 handleCheckout: Error:', err);
      
      // Handle different error types
      if (err.response) {
        // Server responded with error
        const status = err.response.status;
        if (status === 401) {
          setError(t('stripe.errors.notAuthenticated') || 'Please log in to continue');
        } else if (status === 402) {
          setError(t('stripe.errors.paymentRequired') || 'Payment required');
        } else {
          setError(err.response.data?.message || t('stripe.errors.checkoutFailed') || 'Checkout failed');
        }
      } else if (err.request) {
        // No response received
        setError(t('stripe.errors.networkError') || 'Network error. Please check your connection.');
      } else {
        setError(err.message || t('stripe.errors.checkoutFailed') || 'An error occurred');
      }
      
      setLoading(false);
    }
  }, [isAuthenticated, t]);

  /**
   * Button click handler
   */
  const onButtonClick = useCallback((event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    handleCheckout();
  }, [handleCheckout]);

  const handleManageBilling = useCallback(async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!isAuthenticated) {
      const message = t('stripe.product.noPaymentData');
      globalNotifications.error(message);
      setError(message);
      return;
    }

    setPortalLoading(true);
    setError(null);

    try {
      const result = await StripeService.createPortalSession();

      if (result.success && result.url) {
        window.location.href = result.url;
        return;
      }

      const message = t('stripe.product.noPaymentData');
      globalNotifications.error(message);
      setError(message);
      setPortalLoading(false);
    } catch (err) {
      console.error('Portal error:', err);
      const message = t('stripe.product.noPaymentData');
      globalNotifications.error(message);
      setError(message);
      setPortalLoading(false);
    }
  }, [isAuthenticated, t]);

  return (
    <div className="stripe-product-display">
      <div className="subscribe-pricing__card">
        <h2 className="stripe-product-display__product-name">
          {t('stripe.product.title') || 'ArbiWin Premium'}
        </h2>
        <h3 className="subscribe-pricing__title">
          {t('static.subscribe.pricing.title')}
        </h3>
        <div className="subscribe-pricing__price">
          <span className="subscribe-pricing__amount">
            {t('static.subscribe.pricing.amount')} <span className="subscribe-pricing__period">{t('static.subscribe.pricing.period')}</span>
          </span>
        </div>
        <div className="subscribe-pricing__promo">
          <p className="subscribe-pricing__promo-text">{t('static.subscribe.pricing.promoText')}</p>
          <p className="subscribe-pricing__promo-disclaimer">{t('static.subscribe.pricing.promoDisclaimer')}</p>
        </div>
        <h4 className="subscribe-pricing__features-heading">{t('static.subscribe.pricing.featuresIncludes')}</h4>
        <ul className="subscribe-pricing__features">
          <li>{t('static.subscribe.pricing.feature1')}</li>
          <li>{t('static.subscribe.pricing.feature2')}</li>
          <li>{t('static.subscribe.pricing.feature3')}</li>
          <li>{t('static.subscribe.pricing.feature4')}</li>
          <li>{t('static.subscribe.pricing.feature5')}</li>
        </ul>

        {error && (
          <div className="stripe-product-display__error" role="alert">
            {error}
          </div>
        )}

        <Button
          variant="primary"
          size="large"
          onClick={onButtonClick}
          loading={loading}
          disabled={loading || portalLoading || !isAuthenticated}
          className="subscribe-pricing__button"
          fullWidth
          type="button"
        >
          {loading 
            ? (t('stripe.product.processing') || 'Processing...') 
            : (t('stripe.product.checkout') || 'Checkout')
          }
        </Button>
        <button
          type="button"
          className="stripe-product-display__portal-link"
          onClick={handleManageBilling}
          disabled={portalLoading || loading || !isAuthenticated}
        >
          {t('stripe.product.manageBilling')}
        </button>
        <p className="stripe-product-display__security-note">
          {t('stripe.product.securityNote')}
        </p>
      </div>
    </div>
  );
};

export default ProductDisplay;
